import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { useLanguage } from "@/contexts/LanguageContext";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Building2, Calendar, Package } from "lucide-react";
import { sharingService } from "@/services/sharing/sharingService";
import { SharedResultLink } from "@/types/sharing/sharing.types";
import { CarbonResults } from "@/components/results/CarbonResults";
import { HydricResults } from "@/components/results/HydricResults";
import { useData } from "@/hooks/useData";
import { Company } from "@/types/company/company.types";
import { Footer } from "@/components/layout/Footer";

export const SharedResults = () => {
  const { t, setLanguage } = useLanguage();
  const { token } = useParams<{ token: string }>();
  const [sharedLink, setSharedLink] = useState<SharedResultLink | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [logoError, setLogoError] = useState(false);

  // Check for language and PDF mode URL parameters
  const params = new URLSearchParams(window.location.search);
  const langOverride = params.get('lang') as 'en' | 'pt' | null;
  const isPdfMode = params.get('pdf') === 'true';

  // Fetch company data to get logo
  const { data: companies } = useData<Company>({
    collectionName: "companies",
  });

  const company = companies?.find((c) => c.id === sharedLink?.companyId);

  // Set language based on URL parameter
  useEffect(() => {
    if (langOverride && (langOverride === 'en' || langOverride === 'pt')) {
      setLanguage(langOverride);
    }
  }, [langOverride, setLanguage]);

  useEffect(() => {
    const loadSharedLink = async () => {
      if (!token) {
        setError(t("sharedResults.errors.invalidToken"));
        setLoading(false);
        return;
      }

      try {
        const link = await sharingService.getSharedLinkByToken(token);
        
        if (!link) {
          setError(t("sharedResults.errors.linkNotFound"));
          setLoading(false);
          return;
        }

        if (!link.isActive) {
          setError(t("sharedResults.errors.linkDeactivated"));
          setLoading(false);
          return;
        }

        setSharedLink(link);
        
        await sharingService.incrementViewCount(link.id);
      } catch (error) {
        setError(t("sharedResults.errors.loadError"));
      } finally {
        setLoading(false);
      }
    };

    loadSharedLink();
  }, [token, t]);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (error || !sharedLink) {
    return (
      <div className="container mx-auto p-4">
        <div className="text-center py-16">
          <h1 className="text-2xl font-bold text-gray-900 mb-4">
            {t("sharedResults.title")}
          </h1>
          <p className="text-gray-500">{error}</p>
        </div>
      </div>
    );
  }

  const handleLogoError = () => {
    setLogoError(true);
  };

  return (
    <div className={`container mx-auto p-4 ${isPdfMode ? 'pdf-export-mode' : ''}`}>
      {/* Header with Company Logo */}
      <div className="mb-6">
        <div className="flex items-start gap-6">
          {/* Company Logo */}
          {company?.logoUrl && !logoError ? (
            <div className="flex-shrink-0">
              <img
                src={company.logoUrl}
                alt={company.name || sharedLink.companyName}
                className="h-20 w-20 object-contain rounded-lg border border-gray-200 bg-white p-2"
                onError={handleLogoError}
              />
            </div>
          ) : (
            <div className="flex-shrink-0 h-20 w-20 rounded-lg border border-gray-200 bg-gray-50 flex items-center justify-center">
              <Building2 className="h-10 w-10 text-gray-400" />
            </div>
          )}

          {/* Title and Subtitle */}
          <div className="flex-1">
            <h1 className="text-2xl font-bold text-gray-900 mb-2">
              {t("sharedResults.title")}
            </h1>
            <p className="text-gray-600">
              {t("sharedResults.subtitle", { companyName: sharedLink.companyName })}
            </p>
          </div>
        </div>
      </div>

      {/* Company and Product Info */}
      <Card className="mb-6">
        <CardContent className="p-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="flex items-center gap-2">
              <Building2 className="h-5 w-5 text-gray-500" />
              <div>
                <p className="text-sm text-gray-600">{t("sharedResults.company")}</p>
                <p className="font-medium">{sharedLink.companyName}</p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Package className="h-5 w-5 text-gray-500" />
              <div>
                <p className="text-sm text-gray-600">{t("sharedResults.product")}</p>
                <p className="font-medium">{sharedLink.productName}</p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Calendar className="h-5 w-5 text-gray-500" />
              <div>
                <p className="text-sm text-gray-600">{t("sharedResults.year")}</p>
                <p className="font-medium">{sharedLink.year}</p>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Results Tabs */}
      <Card>
        <CardHeader>
          <CardTitle>{t("sharedResults.analysisTitle")}</CardTitle>
        </CardHeader>
        <CardContent>
          {(sharedLink.footprintType === 'carbon' || sharedLink.footprintType === 'both') && (
            <div className="mb-8">
              <h3 className="text-lg font-semibold mb-4">{t("sharedResults.carbonFootprint")}</h3>
              <CarbonResults 
                selectedProduct={sharedLink.productName}
                selectedYear={sharedLink.year.toString()}
                onProductChange={() => {}}
                onYearChange={() => {}}
                isSharedView={true}
                sharedCompanyId={sharedLink.companyId}
              />
            </div>
          )}
          
          {(sharedLink.footprintType === 'hydric' || sharedLink.footprintType === 'both') && (
            <div>
              <h3 className="text-lg font-semibold mb-4">{t("sharedResults.waterFootprint")}</h3>
              <HydricResults 
                selectedProduct={sharedLink.productName}
                selectedYear={sharedLink.year.toString()}
                onProductChange={() => {}}
                onYearChange={() => {}}
                isSharedView={true}
                sharedCompanyId={sharedLink.companyId}
              />
            </div>
          )}
        </CardContent>
      </Card>

      {/* Footer Note */}
      <div className="mt-6 text-center">
        <p className="text-sm text-gray-500">
          {t("sharedResults.footerNote", { companyName: sharedLink.companyName })}
        </p>
      </div>

      {/* Footer with Company Logos */}
      <div className="mt-8">
        <Footer />
      </div>
    </div>
  );
}; 